import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { TID } from "@/constants/testIds";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "./_shared";

export function AmpolImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const submit = async () => {
    if (!file) { toast.error("Please select a CSV file"); return; }
    if (!/^\d{4}-\d{2}$/.test(reportMonth)) { toast.error("Report month must be YYYY-MM"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("report_month", reportMonth);
      const { data } = await api.post("/sustainability/ampol/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.rows_imported} rows · ${fmt(data.totals.co2e_kg)} kg CO₂e`);
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Ampol Monthly Report</DialogTitle>
          <DialogDescription>
            Upload the Ampol CSV export. Columns auto-detected (case-insensitive): Vehicle/Rego, Fuel Type, Litres, Cost ex GST, GST, Total, km, Site, Business Unit, Date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Report Month (YYYY-MM)</label>
            <Input data-testid={TID.sustAmpolReportMonth} value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} placeholder="2026-06" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">CSV File</label>
            <input
              ref={fileInput}
              data-testid={TID.sustAmpolFileInput}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-slate-700 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-emerald-800 file:text-white file:hover:bg-emerald-900"
            />
            {file && <div className="text-xs text-slate-500 mt-1">Selected: {file.name} ({(file.size/1024).toFixed(1)} KB)</div>}
          </div>
          <Badge className="bg-slate-100 text-slate-700 text-xs">
            Re-importing the same month will REPLACE previously imported rows
          </Badge>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid={TID.sustAmpolImportSubmit} onClick={submit} disabled={busy || !file} className="bg-emerald-800 hover:bg-emerald-900 text-white">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</> : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
